/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `UserEditModal` for the Caintra Connect frontend.
 * @module modals/UserEditModal
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import React, { useState, useRef, useEffect, useMemo, useContext } from "react";
import { useNavigate } from "react-router";
import {
  validateEmail,
  validateMobilePhoneNumber,
  validateRFC,
} from "../utils/common.js";
import { validateUserFormFields } from "../utils/userFormValidation.js";
import AssetImage from "../components/AssetImage";
import {
  getUser,
  getUserRoles,
  updateUser,
  getUserIdEditCapabilities,
  filterUserRolesForEdit,
} from "../logic/user/index.js";
import {
  rolesWithAssignedRole,
  selectedEditableRole,
} from "../logic/user/editableRoleSelection.js";
import Select from "react-select";
import CircularProgress from "@mui/material/CircularProgress";
import styles from "../styles/Users.module.css";
import { useDispatch, useSelector } from "react-redux";
import { updateProfileImage } from "../redux/profileSlice";
import { useTranslation } from "react-i18next";
import { AppContext } from "../context/AppContext.js";

import { ReactComponent as DeleteIcon } from "../assets/icons/UserViews/DeleteIcon.svg";
import { ReactComponent as EditIcon } from "../assets/icons/UserViews/EditIcon.svg";
import { ReactComponent as UserNameIcon } from "../assets/icons/UserViews/UserNameIcon.svg";
import { ReactComponent as TitleIcon } from "../assets/icons/UserViews/TitleIcon.svg";
import { ReactComponent as EmailIcon } from "../assets/icons/UserViews/EmailIcon.svg";
import { ReactComponent as RFCIcon } from "../assets/icons/UserViews/RFCIcon.svg";

/**
 * Modal for editing an existing user. Allows updating avatar, user type, name, title,
 * email, phone number, and RFC with validation. Used on desktop when editing a user from
 * the Users page or from the UserDisplayModal edit action.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {function} props.onClose - Called when the modal is closed
 * @param {Object} props.user - The user being edited
 * @param {function} props.onUpdateUser - Called with the updated user when save succeeds
 */
const UserEditModal = ({ open, onClose, user, onUpdateUser }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { permissions } = useContext(AppContext) ?? {};

  // Redux Implementation
  const dispatch = useDispatch();
  const profilePic = useSelector(() => user.imagenDePerfil);

  // Local State
  const [tempImage, setTempImage] = useState(null);
  const [imageKey, setImageKey] = useState(Date.now());
  const [roles, setRoles] = useState([]);
  const [tempUser, setTempUser] = useState({
    tipoDeUsuario: "",
    nombre: "",
    titulo: "",
    correoElectronico: "",
    numeroCelular: "",
    RFC: "",
    imagenDePerfil: "",
    imagenDePerfilFormato: "",
    activo: "",
  });
  const [imagenDePerfil, setProfileImage] = useState(null);
  const [imagenDePerfilFormato, setProfileImageFormat] = useState("");
  const [loading, setLoading] = useState(false);
  const [isAvatarDeleted, setIsAvatarDeleted] = useState(false); // Track avatar deletion
  const [emailError, setEmailError] = useState(false);
  const [emailFormatError, setEmailFormatError] = useState(null);
  const [phoneFormatError, setPhoneFormatError] = useState(null);
  const [rfcFormatError, setRfcFormatError] = useState(null);
  const dialogRef = useRef(null);
  const fileInputRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(null);

  /* --- Helpers and validation --- */

  /**
   * Runs format validation on email, phone (if filled), and RFC (if filled). Sets format error state and returns whether all are valid.
   * @returns {boolean} True if all filled fields pass validation.
   * Sets email/phone/RFC format error state as a side effect.
   */
  const validateFormFields = () => {
    const result = validateUserFormFields({
      correoElectronico: tempUser.correoElectronico,
      numeroCelular: tempUser.numeroCelular,
      RFC: tempUser.RFC,
    });

    setEmailFormatError(result.emailFormatError);
    setPhoneFormatError(result.phoneFormatError);
    setRfcFormatError(result.rfcFormatError);
    return result.valid;
  };

  /**
   * Checks if required fields are present and all filled fields pass format validation (and no RFC error from API).
   * @returns {boolean} True if the form can be submitted.
   */
  const isFormValid = () => {
    if (
      !tempUser.correoElectronico?.trim() ||
      !tempUser.tipoDeUsuario ||
      !tempUser.RFC?.trim()
    ) {
      return false;
    }
    const emailResult = validateEmail(tempUser.correoElectronico);
    if (!emailResult.isValid) {
      return false;
    }
    if (tempUser.numeroCelular?.trim() !== "") {
      const phoneResult = validateMobilePhoneNumber(tempUser.numeroCelular);
      if (!phoneResult.isValid) {
        return false;
      }
    }
    const rfcResult = validateRFC(tempUser.RFC);
    if (!rfcResult.isValid) {
      return false;
    }
    return true;
  };

  /**
   * Fetches the current user by ID and stores it in state (used for permission checks).
   * @returns {Promise<void>}
   */
  const getUserData = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      return;
    }
    const userData = await getUser(userId, navigate);
    setCurrentUser(userData ?? null);
  };

  const editCaps = getUserIdEditCapabilities(currentUser, permissions);
  const isEditingSelf = currentUser?._id === user?._id;
  const noEditOther = editCaps.canEditOnlySelf && !isEditingSelf;
  const limitedFieldsOnly =
    editCaps.canEditLimitedFields ||
    (editCaps.canEditOnlySelf && isEditingSelf);
  const emailDisabled = !editCaps.canEditEmail;
  const canEditTipoDeUsuario = editCaps.canEditTipoDeUsuario;
  const filteredRoles = useMemo(
    () => filterUserRolesForEdit(roles, canEditTipoDeUsuario, user?.RFC),
    [roles, canEditTipoDeUsuario, user?.RFC],
  );
  const displayedUserType = tempUser.tipoDeUsuario || user?.tipoDeUsuario;
  const roleOptions = useMemo(
    () =>
      rolesWithAssignedRole(
        filteredRoles,
        displayedUserType,
        user?.nombreRol,
      ),
    [filteredRoles, displayedUserType, user?.nombreRol],
  );

  /* --- Effects (current user, dialog, form init) --- */

  /**
   * Fetches current user data when the modal opens and user is set (for permission checks).
   * Used to determine primary user and RFC editability.
   */
  useEffect(() => {
    const fetchData = async () => {
      if (open && user) {
        await getUserData();
      }
    };

    fetchData();
  }, [open, user]);

  const handleUpdateUser = async () => {
    if (!validateFormFields() || !user._id) {
      if (!user._id) {
        logger.error("Missing user ID.");
      }
      return;
    }

    const updatedUserData = {
      ...tempUser,
      imagenDePerfil: isAvatarDeleted ? "" : imagenDePerfil,
      imagenDePerfilFormato: isAvatarDeleted ? "" : imagenDePerfilFormato,
    };

    setLoading(true);
    setImageKey(Date.now());

    try {
      await updateUser(user._id, updatedUserData, {
        navigate,
        onSuccess: (updatedUser) => {
          setImageKey(Date.now());
          dispatch(updateProfileImage(updatedUser.imagenDePerfil));
          onUpdateUser(updatedUser);
          onClose();
        },
        onEmailError: () => setEmailError(true),
      });
    } catch (error) {
      logger.error("Error:", { err: sanitizeError(error) });
    } finally {
      setLoading(false);
    }
  };

  /* --- Avatar and form field handlers --- */

  /**
   * Programmatically triggers the hidden file input click to choose an avatar image.
   */
  const handleAvatarEditClick = () => {
    fileInputRef.current.click();
  };

  /**
   * Handles avatar file selection. Accepts JPEG, PNG, WEBP and updates profile image and tempUser state.
   * @param {React.ChangeEvent<HTMLInputElement>} event - Change event from the file input.
   * Resets isAvatarDeleted when a new image is selected.
   */
  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    if (
      file &&
      (file.type === "image/jpeg" ||
        file.type === "image/png" ||
        file.type === "image/webp")
    ) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target.result);
        setProfileImageFormat(file.type.split("/")[1]);
        setTempUser((prev) => ({
          ...prev,
          imagenDePerfil: e.target.result,
          imagenDePerfilFormato: file.type.split("/")[1],
        }));
        setTempImage(e.target.result);
        setIsAvatarDeleted(false);
      };
      reader.readAsDataURL(file);
    } else {
      logger.error(
        "Invalid file type. Please upload a JPEG, PNG, or WEBP image.",
      );
    }
  };

  /**
   * Clears the profile image and sets avatar as deleted so the update sends empty image.
   * Resets imagenDePerfil and imagenDePerfilFormato in tempUser.
   */
  const handleDeleteAvatar = () => {
    setProfileImage(null);
    setProfileImageFormat("");
    setTempUser((prev) => ({
      ...prev,
      imagenDePerfil: "",
      imagenDePerfilFormato: "",
    }));
    setIsAvatarDeleted(true); // Set deletion state
  };

  /**
   * Manages dialog open/close: shows or closes the native dialog and wires close handler to reset temp image and avatar-deleted state.
   * Clears file input when opening.
   */
  useEffect(() => {
    const handleClose = () => {
      setTempImage(null);
      setIsAvatarDeleted(false);
      onClose();
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    if (open) {
      dialog.showModal();
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } else {
      dialog.close();
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
  }, [open, onClose]);

  /**
   * Loads the role catalog only when this editor may change the user type.
   * A locked field uses the edited user's stored role name instead.
   */
  useEffect(() => {
    if (!open || !currentUser) {
      return undefined;
    }
    if (!canEditTipoDeUsuario) {
      setRoles([]);
      return undefined;
    }
    let cancelled = false;
    getUserRoles(navigate).then((list) => {
      if (!cancelled) {
        setRoles(list);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, currentUser, canEditTipoDeUsuario, navigate]);

  /**
   * When modal opens with a user, initializes tempUser and profile image from user and clears format errors.
   * Runs when open or user changes.
   */
  useEffect(() => {
    if (open && user) {
      setTempUser({
        tipoDeUsuario: user.tipoDeUsuario,
        nombre: user.nombre,
        titulo: user.titulo,
        correoElectronico: user.correoElectronico,
        numeroCelular: user.numeroCelular ?? "",
        RFC: user.RFC,
        imagenDePerfil: user.imagenDePerfil,
        imagenDePerfilFormato: user.imagenDePerfilFormato,
        activo: user.activo,
      });
      setProfileImage(user.imagenDePerfil);
      setProfileImageFormat(user.imagenDePerfilFormato);
      setImageKey(Date.now());
      setEmailError(false);
      setEmailFormatError(null);
      setPhoneFormatError(null);
      setRfcFormatError(null);
    }
  }, [open, user]);

  /**
   * Clears the value and related error state for the given field.
   * @param {string} field - Field key in tempUser (e.g. "nombre", "correoElectronico", "numeroCelular", "RFC").
   * Clears related error state for email, phone, and RFC when applicable.
   */
  const handleClearInput = (field) => {
    setTempUser((prev) => ({ ...prev, [field]: "" }));
    if (field === "correoElectronico") {
      setEmailError(false);
      setEmailFormatError(null);
    }
    if (field === "numeroCelular") {
      setPhoneFormatError(null);
    }
    if (field === "RFC") {
      setRfcFormatError(null);
    }
  };

  /**
   * Updates a single field in tempUser and clears its format error if applicable.
   * @param {string} field - Field key in tempUser.
   * @param {*} value - New value for the field.
   * Clears format error for that field when value changes.
   */
  const handleChange = (field, value) => {
    setTempUser((prev) => ({ ...prev, [field]: value }));
    if (field === "correoElectronico") {
      setEmailError(false);
      setEmailFormatError(null);
    }
    if (field === "numeroCelular") {
      setPhoneFormatError(null);
    }
    if (field === "RFC") {
      setRfcFormatError(null);
    }
  };

  const isPrimaryUser = currentUser?.esColaborador === false;
  const tipoDeUsuarioDisabled =
    isPrimaryUser || !canEditTipoDeUsuario || limitedFieldsOnly || noEditOther;
  const nombreDisabled = noEditOther;
  const tituloDisabled = noEditOther;
  const correoDisabled = emailDisabled || limitedFieldsOnly || noEditOther;
  const numeroCelularDisabled = noEditOther;
  const rfcDisabled = isPrimaryUser || limitedFieldsOnly || noEditOther;
  const avatarDisabled = noEditOther || (limitedFieldsOnly && !isEditingSelf);

  return (
    <>
      {open && <div className={styles.overlay}></div>}
      <dialog ref={dialogRef} className={styles.modal}>
        <div className={styles.closeButtonContainer}>
          <button
            className={styles.closeButton}
            onClick={() => {
              onClose();
            }}
          >
            &times;
          </button>
        </div>
        <div className={styles.modalAvatarBoxContainer}>
          <div className={styles.modalAvatarBox}>
            <div className={styles.modalAvatar}>
              {tempUser.imagenDePerfil ? (
                <AssetImage
                  src={tempImage || profilePic || tempUser.imagenDePerfil}
                  alt={t("components.userEditModal.avatarAlt")}
                  className={styles.avatarImage}
                />
              ) : (
                tempUser.nombre.charAt(0)
              )}
            </div>
            {!avatarDisabled && (
              <div className={styles.modalAvatarButtons}>
                <button
                  className={styles.modalAvatarEditButton}
                  onClick={handleAvatarEditClick}
                >
                  <EditIcon />
                </button>
                <button
                  className={styles.modalAvatarDeleteButton}
                  onClick={handleDeleteAvatar}
                >
                  <DeleteIcon />
                </button>
              </div>
            )}
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept="image/jpeg, image/png, image/webp"
              onChange={handleAvatarChange}
            />
          </div>
        </div>

        <div
          className={`${styles.modalContent} ${styles.userCreationModalCompact}`}
        >
          <label className={styles.modalLabel}>
            {t("components.userEditModal.userType")}
          </label>
          <Select
            options={roleOptions}
            value={selectedEditableRole(
              roleOptions,
              displayedUserType,
              user?.nombreRol,
            )}
            onChange={(selectedOption) =>
              handleChange("tipoDeUsuario", selectedOption.value)
            }
            placeholder={t("components.userEditModal.userTypePlaceholder")}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                borderRadius: "8px",
                width: "100%",
                height: "30px",
                fontSize: "16px",
                cursor: "pointer",
                marginBottom: "4px",
                borderColor: !tempUser.tipoDeUsuario
                  ? "red"
                  : baseStyles.borderColor,
              }),
              container: (baseStyles) => ({
                ...baseStyles,
              }),
              valueContainer: (baseStyles) => ({
                ...baseStyles,
              }),
              input: (baseStyles) => ({
                ...baseStyles,
              }),
              menu: (baseStyles) => ({
                ...baseStyles,
                zIndex: 9999,
              }),
              menuPortal: (baseStyles) => ({
                ...baseStyles,
                zIndex: 9999,
              }),
              option: (baseStyles) => ({
                ...baseStyles,
                cursor: "pointer",
              }),
              indicatorSeparator: () => ({ display: "none" }),
            }}
            isDisabled={tipoDeUsuarioDisabled}
          />

          <label className={styles.modalLabel}>
            {t("components.userEditModal.userName")}
          </label>
          <div className={styles.inputContainer}>
            <div className={styles.inputWrapper}>
              <div className={styles.inputIcon}>
                <UserNameIcon />
              </div>
              <input
                type="text"
                placeholder={t("components.userEditModal.userNamePlaceholder")}
                value={tempUser.nombre}
                onChange={(e) => handleChange("nombre", e.target.value)}
                className={styles.modalInput}
                disabled={nombreDisabled}
              />
              {tempUser.nombre && !nombreDisabled && (
                <button
                  className={styles.inputClearButton}
                  onClick={() => handleClearInput("nombre")}
                >
                  &times;
                </button>
              )}
            </div>
          </div>

          <hr className={styles.divider} />

          <label className={styles.modalLabel}>
            {t("components.userEditModal.title")}
          </label>
          <div className={styles.inputContainer}>
            <div className={styles.inputWrapper}>
              <div className={styles.inputIcon}>
                <TitleIcon />
              </div>
              <input
                type="text"
                placeholder={t("components.userEditModal.titlePlaceholder")}
                value={tempUser.titulo}
                onChange={(e) => handleChange("titulo", e.target.value)}
                className={styles.modalInput}
                disabled={tituloDisabled}
              />
              {tempUser.titulo && !tituloDisabled && (
                <button
                  className={styles.inputClearButton}
                  onClick={() => handleClearInput("titulo")}
                >
                  &times;
                </button>
              )}
            </div>
          </div>

          <label className={styles.modalLabel}>
            {t("components.userEditModal.email")}
          </label>
          <div className={styles.inputContainer}>
            <div className={styles.inputWrapper}>
              <div className={styles.inputIcon}>
                <EmailIcon />
              </div>
              <input
                type="email"
                placeholder={t("components.userEditModal.emailPlaceholder")}
                value={tempUser.correoElectronico}
                onChange={(e) =>
                  handleChange("correoElectronico", e.target.value)
                }
                onBlur={() => {
                  if (tempUser.correoElectronico.trim() === "") {
                    setEmailFormatError(null);
                    return;
                  }
                  const result = validateEmail(tempUser.correoElectronico);
                  setEmailFormatError(result.isValid ? null : result.error);
                }}
                className={styles.modalInput}
                style={{
                  borderColor:
                    tempUser.correoElectronico.trim() !== "" &&
                    (emailError || emailFormatError)
                      ? "red"
                      : "",
                }}
                disabled={correoDisabled}
              />
              {tempUser.correoElectronico && !correoDisabled && (
                <button
                  className={styles.inputClearButton}
                  onClick={() => handleClearInput("correoElectronico")}
                >
                  &times;
                </button>
              )}
            </div>
          </div>

          <label className={styles.modalLabel}>
            {t("components.userEditModal.phone")}
          </label>
          <div className={styles.inputContainer}>
            <div className={styles.inputWrapper}>
              <input
                type="tel"
                placeholder={t("components.userEditModal.phonePlaceholder")}
                value={tempUser.numeroCelular}
                onChange={(e) => handleChange("numeroCelular", e.target.value)}
                onBlur={() => {
                  if (tempUser.numeroCelular.trim() === "") {
                    setPhoneFormatError(null);
                    return;
                  }
                  const result = validateMobilePhoneNumber(
                    tempUser.numeroCelular,
                  );
                  setPhoneFormatError(result.isValid ? null : result.error);
                }}
                className={styles.modalInput}
                style={{
                  borderColor:
                    tempUser.numeroCelular.trim() !== "" && phoneFormatError
                      ? "red"
                      : "",
                }}
                disabled={numeroCelularDisabled}
              />
              {tempUser.numeroCelular && !numeroCelularDisabled && (
                <button
                  className={styles.inputClearButton}
                  onClick={() => handleClearInput("numeroCelular")}
                >
                  &times;
                </button>
              )}
            </div>
          </div>

          <label className={styles.modalLabel}>
            {t("components.userEditModal.rfc")}
          </label>
          <div className={styles.inputContainer}>
            <div className={styles.inputWrapper}>
              <div className={styles.inputIcon}>
                <RFCIcon />
              </div>
              <input
                type="text"
                placeholder={t("components.userEditModal.rfcPlaceholder")}
                value={tempUser.RFC}
                onChange={(e) => handleChange("RFC", e.target.value)}
                onBlur={() => {
                  if (tempUser.RFC.trim() === "") {
                    setRfcFormatError(null);
                    return;
                  }
                  const result = validateRFC(tempUser.RFC);
                  setRfcFormatError(result.isValid ? null : result.error);
                }}
                className={styles.modalInput}
                style={{
                  borderColor:
                    tempUser.RFC.trim() !== "" && rfcFormatError ? "red" : "",
                }}
                disabled={rfcDisabled}
              />
              {tempUser.RFC && !rfcDisabled && (
                <button
                  className={styles.inputClearButton}
                  onClick={() => handleClearInput("RFC")}
                >
                  &times;
                </button>
              )}
            </div>
          </div>
        </div>

        <div className={styles.modalButtons}>
          <button
            onClick={() => {
              onClose();
            }}
            className={styles.cancelButton}
          >
            {t("components.userEditModal.cancel")}
          </button>
          <button
            className={styles.createUserButton}
            onClick={handleUpdateUser}
            disabled={loading || !isFormValid() || noEditOther}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              t("components.userEditModal.save")
            )}
          </button>
        </div>
      </dialog>
    </>
  );
};

export default UserEditModal;
